'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'next/navigation';
import { useKeenSlider } from 'keen-slider/react';
import {
    Building2,
    Calendar,
    CircleUser,
    LocateFixed,
    MapPin,
    Plus,
    RadioTower,
    Smile,
    TableCellsSplit,
    Undo2,
    Warehouse,
    Wifi,
} from 'lucide-react';
import mapboxgl from 'mapbox-gl';
import { createRoot } from 'react-dom/client';
import { useHubNewsList } from '@/hooks/feature/hubs/useHubNewsList';
import { useHandleError } from '@/hooks/useHandleError';
import { useDeviceType } from '@/hooks/useIsMobile';
import { apiFetch, apiFetchWithQuery } from '@/lib/api/apiClient';
import { useModalSlideStore } from '@/stores/useModalSlideStore';
import { MAPBOX_ACCESS_TOKEN } from '@/constants/constants';
import { HubMapItem, HubNewsItem, HubType, HubTypeConfig, HubTypeItem } from '@/types/hubs';
import { buildQueryParams } from '@/utils/helpers/queryHelper';
import IconAdaptive from '@/components/IconAdaptive';
import HubMapSearch from '@/(routers)/hubs/map/components/HubMapSearch';
import HubMapPinButtonUnit from '@/(routers)/hubs/map/components/unit/HubMapPinButtonUnit';
import HubMapPopupUnit from '@/(routers)/hubs/map/components/unit/HubMapPopupUnit';
import HubNewsHubView from '@/(routers)/hubs/cases/components/HubNewsHubView';
import 'keen-slider/keen-slider.min.css';
import 'mapbox-gl/dist/mapbox-gl.css';

mapboxgl.accessToken = MAPBOX_ACCESS_TOKEN;

const JAPANESE_LABEL_LAYERS = ['road-motorway-trunk', 'road-primary', 'road-number-shield'];
const INITIAL_VIEW_STATE = {
    zoom: 5.1,
    pitch: 0,
    bearing: 0,
};

const CENTER_PC: [number, number] = [137.04604, 37.5313];
const CENTER_MOBILE: [number, number] = [137.0186, 37.1749];
const MAP_VIEWPORT_HEIGHT = 'calc(100vh - 85px)';

const HUB_TYPE_TO_ICON: Record<string, string> = {
    spot: 'icon-spot',
    festa: 'icon-festa',
    stand: 'icon-stand',
    studio: 'icon-studio',
    schoomyers: 'icon-schoomyers',
    crew: 'icon-crew',
    stage: 'icon-stage',
    in: 'icon-in',
};

export default function HubMap() {
    const { isMobile, isTablet, isDesktop } = useDeviceType();
    const { handleError, clearError } = useHandleError();
    const initialCenter = isMobile ? CENTER_MOBILE : CENTER_PC;
    const [centerCoord, setCenterCoord] = useState<[number, number]>(initialCenter);
    const mapContainer = useRef<HTMLDivElement>(null);
    const mapRef = useRef<mapboxgl.Map | null>(null);
    const currentHubMarkerRef = useRef<mapboxgl.Marker | null>(null);
    const [isMapReady, setIsMapReady] = useState(false);

    const [isSpot, setIsSpot] = useState(true);
    const [isFesta, setIsFesta] = useState(true);
    const [isStand, setIsStand] = useState(true);
    const [isStudio, setIsStudio] = useState(true);
    const [isSchoomyers, setIsSchoomyers] = useState(true);
    const [isCrew, setIsCrew] = useState(true);
    const [isStage, setIsStage] = useState(true);
    const [isIn, setIsIn] = useState(true);
    const [hoveredAll, setHoveredAll] = useState(false);
    const isAll = isSpot && isFesta && isStand && isStudio && isSchoomyers && isCrew && isStage && isIn;

    const setAllOn = useCallback(() => {
        setIsSpot(true);
        setIsFesta(true);
        setIsStand(true);
        setIsStudio(true);
        setIsSchoomyers(true);
        setIsCrew(true);
        setIsStage(true);
        setIsIn(true);
    }, []);

    const isolateColor = useCallback((key: HubType) => {
        setIsSpot(key === HubType.spot);
        setIsFesta(key === HubType.festa);
        setIsStand(key === HubType.stand);
        setIsStudio(key === HubType.studio);
        setIsSchoomyers(key === HubType.schoomyers);
        setIsCrew(key === HubType.crew);
        setIsStage(key === HubType.stage);
        setIsIn(key === HubType.in);
    }, []);

    const [hoveredSpot, setHoveredSpot] = useState(false);
    const [hoveredFesta, setHoveredFesta] = useState(false);
    const [hoveredStand, setHoveredStand] = useState(false);
    const [hoveredStudio, setHoveredStudio] = useState(false);
    const [hoveredSchoomyers, setHoveredSchoomyers] = useState(false);
    const [hoveredCrew, setHoveredCrew] = useState(false);
    const [hoveredStage, setHoveredStage] = useState(false);
    const [hoveredIn, setHoveredIn] = useState(false);
    const [isSatellite, setIsSatellite] = useState(false);
    const [hubTypeConfigList, setHubTypeConfigList] = useState<HubTypeConfig[]>([]);
    const [hubMap, setHubMap] = useState<HubMapItem[]>([]);
    const [selectedHubIndex, setSelectedHubIndex] = useState<string | null>(null);
    const activeHubPopupRef = useRef<mapboxgl.Popup | null>(null);
    const searchParams = useSearchParams();
    const redirectHubIndex = searchParams.get('hid');
    const { hubNewsList } = useHubNewsList();
    const { openModalSlide } = useModalSlideStore();
    const hubNewsListRef = useRef<HubNewsItem[]>(hubNewsList);
    const activeHubPopupRenderRef = useRef<(() => void) | null>(null);

    useEffect(() => {
        hubNewsListRef.current = hubNewsList;
        activeHubPopupRenderRef.current?.();
    }, [hubNewsList]);

    const getHubNewsListByHubIndex = useCallback(
        (hubIndex: string) => hubNewsListRef.current.filter((hubNews) => hubNews.hub_index === hubIndex),
        [],
    );

    const openHubNewsSlide = useCallback(
        (hubIndex: string) => {
            const newsListOfHub = getHubNewsListByHubIndex(hubIndex);
            if (newsListOfHub.length === 0) return;
            openModalSlide({
                id: 'hub-news-hub',
                direction: 'right',
                size: { pcWidth: '680px', mbWidth: '100%', height: '100%' },
                component: <HubNewsHubView hubNews={newsListOfHub[0]} hubNewsList={newsListOfHub} variant="slide" />,
            });
        },
        [getHubNewsListByHubIndex, openModalSlide],
    );

    const HUB_TYPE_UI_PRESET: Omit<HubTypeConfig, 'isVisibleOnMap'>[] = [
        {
            type: HubType.spot,
            label: 'SPOT',
            icon: RadioTower,
            colorClass: 'hub-spot-pin',
            mobileWidth: 80,
            hoveredWidth: 100,
        },
        {
            type: HubType.in,
            label: 'IN',
            icon: Wifi,
            colorClass: 'hub-in-pin',
            mobileWidth: 60,
            hoveredWidth: 80,
        },
        {
            type: HubType.stage,
            label: 'STAGE',
            icon: Building2,
            colorClass: 'hub-stage-pin',
            mobileWidth: 80,
            hoveredWidth: 100,
        },
        {
            type: HubType.festa,
            label: 'FESTA',
            icon: Calendar,
            colorClass: 'hub-festa-pin',
            mobileWidth: 80,
            hoveredWidth: 100,
        },
        {
            type: HubType.stand,
            label: 'STAND',
            icon: TableCellsSplit,
            colorClass: 'hub-stand-pin',
            mobileWidth: 85,
            hoveredWidth: 100,
        },
        {
            type: HubType.studio,
            label: 'STUDIO',
            icon: Warehouse,
            colorClass: 'hub-studio-pin',
            mobileWidth: 90,
            hoveredWidth: 100,
        },
        {
            type: HubType.schoomyers,
            label: 'SCHOOMYERS',
            icon: Smile,
            colorClass: 'hub-schoomyers-pin',
            mobileWidth: 130,
            hoveredWidth: 150,
        },
        {
            type: HubType.crew,
            label: 'CREW',
            icon: CircleUser,
            colorClass: 'hub-crew-pin',
            mobileWidth: 80,
            hoveredWidth: 100,
        },
    ];

    useEffect(() => {
        const map = mapRef.current;
        if (!map) return;

        map.flyTo({
            ...INITIAL_VIEW_STATE,
            center: initialCenter,
            essential: true,
        });
    }, [isMobile, isTablet, isDesktop]);

    // APIからハブタイプ表示を取得
    useEffect(() => {
        if (!isMapReady) return;

        const fetchHubType = async () => {
            clearError();
            try {
                const res = await apiFetch(`/hubs/types`, { method: 'GET' });
                const HubTypeList: HubTypeItem[] = res.hub_type_list;

                const merged: HubTypeConfig[] = HUB_TYPE_UI_PRESET.map((ui) => {
                    const api = HubTypeList.find((t) => t.name_en === ui.type);

                    return {
                        ...ui,
                        isVisibleOnMap: api?.is_visible_on_map ?? false,
                    };
                });

                setHubTypeConfigList(merged);
            } catch (error: unknown) {
                handleError(error);
            }
        };

        fetchHubType();
    }, [isMapReady]);

    // APIから地図情報を取得
    useEffect(() => {
        if (!isMapReady) return;
        const fetchHubMap = async () => {
            clearError();
            try {
                const res = await apiFetchWithQuery(
                    `/hubs/map`,
                    buildQueryParams({
                        is_spot: isSpot,
                        is_festa: isFesta,
                        is_stand: isStand,
                        is_studio: isStudio,
                        is_schoomyers: isSchoomyers,
                        is_crew: isCrew,
                        is_stage: isStage,
                        is_in: isIn,
                    }),
                    {
                        method: 'GET',
                    },
                );
                setHubMap(res.hub_map_list);
            } catch (error: unknown) {
                handleError(error);
            }
        };
        fetchHubMap();
    }, [isMapReady, isSpot, isFesta, isStand, isStudio, isSchoomyers, isCrew, isStage, isIn, isSatellite]);

    // 地図スタイルが読み込まれたときに、日本語ラベル適用 + 不要レイヤー非表示
    const applyJapaneseLabelsAndHideLayers = useCallback(() => {
        const map = mapRef.current;
        const layers = map?.getStyle().layers;
        if (!layers) return;

        layers.forEach((layer) => {
            if (layer.type === 'symbol' && layer.layout && 'text-field' in layer.layout) {
                map?.setLayoutProperty(layer.id, 'text-field', ['get', 'name_ja']);
            }
        });

        JAPANESE_LABEL_LAYERS.forEach((layerId) => {
            if (map?.getLayer(layerId)) {
                map.setLayoutProperty(layerId, 'visibility', 'none');
            }
        });
    }, []);

    // 現在位置の設定
    const handleLocateMe = useCallback(() => {
        if (!navigator.geolocation) return;

        navigator.geolocation.getCurrentPosition(
            ({ coords }) => {
                if (!mapRef.current) return;

                currentHubMarkerRef.current?.remove();

                currentHubMarkerRef.current = new mapboxgl.Marker({ color: 'blue' })
                    .setLngLat([coords.longitude, coords.latitude])
                    .setPopup(new mapboxgl.Popup().setText('現在地'))
                    .addTo(mapRef.current);

                mapRef.current.flyTo({
                    center: [coords.longitude, coords.latitude],
                    zoom: 13,
                    speed: 1.2,
                });
            },
            () => {},
            {
                enableHighAccuracy: true,
                timeout: 5000,
                maximumAge: 0,
            },
        );
    }, []);

    // 地図位置の初期化設定
    const handleResetView = () => {
        if (!mapRef.current) return;

        mapRef.current.flyTo({
            ...INITIAL_VIEW_STATE,
            center: initialCenter,
            speed: 1.2,
            essential: true,
        });
    };

    useEffect(() => {
        activeHubPopupRef.current?.remove();
        setSelectedHubIndex(null);
    }, [isSpot, isFesta, isStudio, isStand, isSchoomyers, isCrew, isStage, isIn]);

    // ウィンドウサイズ変更後に地図をリサイズ
    useEffect(() => {
        let timeout: ReturnType<typeof setTimeout>;

        const handleResize = () => {
            clearTimeout(timeout);
            timeout = setTimeout(() => {
                mapRef.current?.resize();
            }, 300);
        };

        window.addEventListener('resize', handleResize);
        return () => {
            window.removeEventListener('resize', handleResize);
            clearTimeout(timeout);
        };
    }, []);

    // ズームを調整
    useEffect(() => {
        const map = mapRef.current;
        if (!map || !map.isStyleLoaded()) return;

        const newZoom = isMobile ? 4.2 : isTablet ? 4.8 : isDesktop ? 5.4 : 5.6;

        if (Math.abs(map.getZoom() - newZoom) > 0.01) {
            map.easeTo({
                zoom: newZoom,
                duration: 500,
            });
        }
    }, [isMobile, isTablet, isDesktop]);

    // 初期化処理
    useEffect(() => {
        if (mapRef.current || !mapContainer.current) return;

        const map = new mapboxgl.Map({
            container: mapContainer.current,
            style: 'mapbox://styles/mapbox/streets-v12',
            ...INITIAL_VIEW_STATE,
            center: initialCenter,
            antialias: true,
        });
        mapRef.current = map;

        // スタイル読込時：日本語ラベル適用 → ready フラグ → 次フレームで resize
        map.on('style.load', () => {
            applyJapaneseLabelsAndHideLayers();
            setIsMapReady(true);
            requestAnimationFrame(() => map.resize());
        });

        // マップ本体の load 完了時にも保険で resize
        map.once('load', () => {
            requestAnimationFrame(() => map.resize());
        });

        map.on('moveend', () => {
            const center = map.getCenter();
            setCenterCoord([center.lng, center.lat]);
        });

        return () => {
            map.remove();
            mapRef.current = null;
        };
    }, [applyJapaneseLabelsAndHideLayers]);

    useEffect(() => {
        if (!mapRef.current || hubMap.length === 0) return;
        const pinHubs = hubMap.filter((h) => h.lat !== null && h.lng !== null);
        const map = mapRef.current;
        const geoJson: GeoJSON.FeatureCollection = {
            type: 'FeatureCollection',
            features: pinHubs.map((hub) => ({
                type: 'Feature',
                geometry: {
                    type: 'Point',
                    coordinates: [hub.lng, hub.lat],
                },
                properties: {
                    hubType: hub.hub_type,
                    hubIndex: hub.hub_index,
                    hubName: hub.hub_name,
                    hubSubName: hub.hub_sub_name,
                    prefectureName: hub.prefecture_name,
                    countryCd: hub.country_cd,
                    icon: HUB_TYPE_TO_ICON[hub.hub_type] ?? 'default-icon',
                },
            })),
        };

        const loadIcon = (id: string, url: string) =>
            new Promise<void>((resolve, reject) => {
                if (map.hasImage(id)) return resolve();
                map.loadImage(url, (err, img) => {
                    if (err || !img) return reject(err);
                    map.addImage(id, img);
                    resolve();
                });
            });

        const setupMap = async () => {
            try {
                await Promise.all([
                    loadIcon('icon-spot', '/images/hub/map/pin-spot.png'),
                    loadIcon('icon-festa', '/images/hub/map/pin-festa.png'),
                    loadIcon('icon-stand', '/images/hub/map/pin-stand.png'),
                    loadIcon('icon-studio', '/images/hub/map/pin-studio.png'),
                    loadIcon('icon-schoomyers', '/images/hub/map/pin-schoomyers.png'),
                    loadIcon('icon-crew', '/images/hub/map/pin-crew.png'),
                    loadIcon('icon-stage', '/images/hub/map/pin-stage.png'),
                    loadIcon('icon-in', '/images/hub/map/pin-in.png'),
                ]);

                if (map.getLayer('hub-layer')) map.removeLayer('hub-layer');
                if (map.getSource('hub-source')) map.removeSource('hub-source');

                map.addSource('hub-source', {
                    type: 'geojson',
                    data: geoJson,
                });

                map.addLayer({
                    id: 'hub-layer',
                    type: 'symbol',
                    source: 'hub-source',
                    layout: {
                        'icon-image': ['get', 'icon'],
                        'icon-size': isMobile ? 0.4 : 0.55,
                        'icon-allow-overlap': true,
                        'icon-ignore-placement': true,
                        'icon-anchor': 'bottom',
                    },
                });
                map.on('click', 'hub-layer', (e) => {
                    if (!e.features || !e.features.length) return;
                    const feature = e.features[0];
                    const coordinates = (feature.geometry as any).coordinates.slice();
                    const { hubType, hubIndex, hubName, hubSubName, countryCd, prefectureName, icon } =
                        feature.properties as any;
                    const onMoveEnd = () => {
                        activeHubPopupRef.current?.remove();
                        const popupNode = document.createElement('div');
                        const hubIndexKey = String(hubIndex);
                        const popupRoot = createRoot(popupNode);
                        const renderPopup = () => {
                            popupRoot.render(
                                <HubMapPopupUnit
                                    hubType={hubType}
                                    hubName={hubName}
                                    hubSubName={hubSubName}
                                    countryCd={countryCd}
                                    prefectureName={prefectureName}
                                    icon={icon}
                                    hubNewsCount={getHubNewsListByHubIndex(hubIndexKey).length}
                                    onOpenHubNews={() => openHubNewsSlide(hubIndexKey)}
                                />,
                            );
                        };
                        renderPopup();
                        activeHubPopupRenderRef.current = renderPopup;
                        setSelectedHubIndex(hubIndex);
                        const popup = new mapboxgl.Popup({ offset: 60, maxWidth: '800px' })
                            .setLngLat(coordinates as [number, number])
                            .setDOMContent(popupNode)
                            .addTo(map);
                        popup.getElement()?.style.setProperty('z-index', '30');
                        activeHubPopupRef.current = popup;
                        popup.on('close', () => {
                            setSelectedHubIndex(null);
                            activeHubPopupRef.current = null;
                            activeHubPopupRenderRef.current = null;
                        });
                        map.off('moveend', onMoveEnd);
                    };
                    map.off('moveend', onMoveEnd);
                    map.on('moveend', onMoveEnd);

                    map.flyTo({
                        center: coordinates,
                        zoom: map.getZoom(),
                        speed: 1.2,
                        essential: true,
                        offset: [0, 100],
                    });
                });
                map.on('mouseenter', 'hub-layer', () => {
                    map.getCanvas().style.cursor = 'pointer';
                });
                map.on('mouseleave', 'hub-layer', () => {
                    map.getCanvas().style.cursor = '';
                });
            } catch (err) {}
        };
        setupMap();

        return () => {
            if (!mapRef.current) return;
            if (map.getLayer('hub-layer')) map.removeLayer('hub-layer');
            if (map.getSource('hub-source')) map.removeSource('hub-source');
        };
    }, [hubMap, isMobile]);

    const handleSelectHub = async () => {
        const map = mapRef.current;
        const hub = hubMap.find((h) => h.hub_index.toString() === selectedHubIndex);
        if (!hub || !map) return;

        activeHubPopupRef.current?.remove();
        setSelectedHubIndex(hub.hub_index);
        if (!hub.is_online) {
            const popupNode = document.createElement('div');
            const popupRoot = createRoot(popupNode);
            const renderPopup = () => {
                popupRoot.render(
                    <HubMapPopupUnit
                        hubType={hub.hub_type}
                        hubName={hub.hub_name}
                        hubSubName={hub.hub_sub_name}
                        countryCd={hub.country_cd}
                        prefectureName={hub.prefecture_name}
                        icon={HUB_TYPE_TO_ICON[hub.hub_type] ?? 'default-icon'}
                        hubNewsCount={getHubNewsListByHubIndex(String(hub.hub_index)).length}
                        onOpenHubNews={() => openHubNewsSlide(String(hub.hub_index))}
                    />,
                );
            };
            renderPopup();
            activeHubPopupRenderRef.current = renderPopup;
            const popup = new mapboxgl.Popup({ offset: 60, maxWidth: '800px' })
                .setLngLat([hub.lng, hub.lat])
                .setDOMContent(popupNode)
                .addTo(map)
                .on('close', () => {
                    setSelectedHubIndex(null);
                    activeHubPopupRenderRef.current = null;
                });

            activeHubPopupRef.current = popup;
            mapRef.current?.flyTo({
                center: [hub.lng, hub.lat],
                zoom: 14,
                speed: 1.2,
            });
        } else {
            handleResetView();
        }
    };

    useEffect(() => {
        if (selectedHubIndex && hubMap.length > 0 && isMapReady) {
            handleSelectHub();
        }
    }, [selectedHubIndex, hubMap, isMapReady]);

    useEffect(() => {
        if (redirectHubIndex) {
            setSelectedHubIndex(redirectHubIndex.toString());
        }
    }, [redirectHubIndex]);

    const [sliderRef, instanceRef] = useKeenSlider<HTMLDivElement>({
        slides: {
            perView: 'auto',
            spacing: 6,
        },
        mode: 'free',
        renderMode: 'performance',
    });

    useEffect(() => {
        if (instanceRef.current) {
            instanceRef.current.update();
        }
    }, [instanceRef, hubTypeConfigList, isMobile]);

    const PC_BOTTOM_BASE = 118;
    const PC_BOTTOM_GAP = 40;
    const PC_BOTTOM_BASE_MD = 98;
    const PC_BOTTOM_GAP_MD = 40;

    const hoverMap = {
        all: [hoveredAll, setHoveredAll],
        [HubType.spot]: [hoveredSpot, setHoveredSpot],
        [HubType.festa]: [hoveredFesta, setHoveredFesta],
        [HubType.stand]: [hoveredStand, setHoveredStand],
        [HubType.studio]: [hoveredStudio, setHoveredStudio],
        [HubType.schoomyers]: [hoveredSchoomyers, setHoveredSchoomyers],
        [HubType.crew]: [hoveredCrew, setHoveredCrew],
        [HubType.stage]: [hoveredStage, setHoveredStage],
        [HubType.in]: [hoveredIn, setHoveredIn],
    } as const;

    const buttonConfigs = [
        {
            key: 'all',
            type: 'all',
            label: 'ALL',
            icon: MapPin,
            colorClass: 'base',
            active: isAll,
            onClick: setAllOn,
            mobileWidth: 70,
            hoveredWidth: 90,
            onResetView: handleResetView,
        },
        ...hubTypeConfigList
            .filter((cfg) => cfg.isVisibleOnMap)
            .map((cfg) => ({
                key: cfg.type,
                type: cfg.type,
                label: cfg.label,
                icon: cfg.icon,
                colorClass: cfg.colorClass,
                active:
                    (cfg.type === HubType.spot && isSpot) ||
                    (cfg.type === HubType.festa && isFesta) ||
                    (cfg.type === HubType.stand && isStand) ||
                    (cfg.type === HubType.studio && isStudio) ||
                    (cfg.type === HubType.schoomyers && isSchoomyers) ||
                    (cfg.type === HubType.crew && isCrew) ||
                    (cfg.type === HubType.stage && isStage) ||
                    (cfg.type === HubType.in && isIn),
                onClick: () => isolateColor(cfg.type),
                mobileWidth: cfg.mobileWidth,
                hoveredWidth: cfg.hoveredWidth,
                onResetView: undefined,
            })),
    ].map((btn, index, arr) => {
        const [hovered, setHovered] = hoverMap[btn.type as keyof typeof hoverMap];
        const reverseIndex = arr.length - 1 - index;

        return {
            ...btn,
            hovered,
            setHovered,
            positionBottom: PC_BOTTOM_BASE + reverseIndex * PC_BOTTOM_GAP,
            positionBottomMd: PC_BOTTOM_BASE_MD + reverseIndex * PC_BOTTOM_GAP_MD,
        };
    });

    return (
        <>
            {/* <ServiceHeader title="MAP." /> */}
            <div className="flex justify-center w-full">
                <div className="flex flex-col items-center w-full">
                    <div
                        className="w-full relative text-color-base border-none pt-0 overflow-hidden rounded-md"
                        style={{ height: MAP_VIEWPORT_HEIGHT }}
                    >
                        <div ref={mapContainer} className="absolute inset-0 w-full h-full" />
                        <div className="pointer-events-none absolute z-50 top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-xl text-color-white-base select-none">
                            <IconAdaptive Icon={Plus} baseSize={20} />
                        </div>

                        {/* ハブ名検索フォーム */}
                        <div className="absolute top-1 md:top-2 left-1 md:left-2 z-10">
                            <HubMapSearch hubMaps={hubMap} setSelectedHubIndex={setSelectedHubIndex} />
                        </div>

                        {/* 各種ピン */}
                        {isMobile || isTablet ? (
                            <div className="absolute top-[60px] md:top-[80px] left-1 md:left-2 z-10 w-[95%]">
                                <div ref={sliderRef} className="keen-slider">
                                    {buttonConfigs.map((btn) => (
                                        <HubMapPinButtonUnit
                                            key={btn.key}
                                            label={btn.label}
                                            icon={btn.icon}
                                            active={btn.active}
                                            colorClass={btn.colorClass}
                                            hovered={btn.hovered}
                                            setHovered={btn.setHovered}
                                            onClick={btn.onClick}
                                            onResetView={btn.onResetView}
                                            isMobile={isMobile}
                                            isTablet={isTablet}
                                            mobileWidth={btn.mobileWidth}
                                            positionBottom={btn.positionBottom}
                                            positionBottomMd={btn.positionBottomMd}
                                            hoveredWidth={btn.hoveredWidth}
                                        />
                                    ))}
                                </div>
                            </div>
                        ) : (
                            <>
                                {buttonConfigs.map((btn) => (
                                    <HubMapPinButtonUnit
                                        key={btn.key}
                                        label={btn.label}
                                        icon={btn.icon}
                                        active={btn.active}
                                        colorClass={btn.colorClass}
                                        hovered={btn.hovered}
                                        setHovered={btn.setHovered}
                                        onClick={btn.onClick}
                                        onResetView={btn.onResetView}
                                        isMobile={isMobile}
                                        isTablet={isTablet}
                                        mobileWidth={btn.mobileWidth}
                                        positionBottom={btn.positionBottom}
                                        positionBottomMd={btn.positionBottomMd}
                                        hoveredWidth={btn.hoveredWidth}
                                    />
                                ))}
                            </>
                        )}

                        {/* 操作ボタン群 */}
                        <div className="absolute bottom-[40px] md:bottom-[23px] right-1 md:right-2 z-10 flex flex-col gap-3 bg-color-white-base backdrop-blur-md p-2 rounded shadow-md">
                            <IconAdaptive
                                Icon={LocateFixed}
                                baseSize={20}
                                breakpoints={{ md: 20 }}
                                className="cursor-pointer hover:text-color-service-selected"
                                onClick={handleLocateMe}
                            />
                            <IconAdaptive
                                Icon={Undo2}
                                baseSize={20}
                                breakpoints={{ md: 20 }}
                                className="cursor-pointer hover:text-color-service-selected"
                                onClick={handleResetView}
                            />
                        </div>

                        {/* 緯度経度 */}
                        <div className="absolute bottom-2 left-2 z-10 bg-white/80 text-xs px-2 py-1 rounded shadow">
                            緯度: {centerCoord[1].toFixed(5)} 　 経度: {centerCoord[0].toFixed(5)}
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
}
